import { Info } from 'lucide-react'
import { useEffect, useState } from 'react'

import type {
  InterestPostingFrequency,
  SavingsProductClass,
} from '@/api/savings-types'
import type { DualControlResult } from '@/api/types'
import { MoneyInput } from '@/components/money-input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BASE_CURRENCY, type Pesewas } from '@/lib/money'

import {
  INTEREST_POSTING_FREQUENCIES,
  SAVINGS_PRODUCT_CLASSES,
  interestFrequencyLabel,
  productClassDescription,
  productClassLabel,
} from './constants'
import { SavingsDualControlNotice, SavingsDualControlOutcome } from './dual-control'
import { useCreateSavingsProduct } from './mutations'
import { SAVINGS_PERMISSIONS, useSavingsPermissions } from './permissions'
import { SavingsActionDialog } from './savings-action-dialog'

/**
 * Defining a product.
 *
 * Dual-controlled, and permanently consequential: the rate, the fees, the dormancy period and the
 * three general-ledger accounts here are carried by every account ever opened against it, and the
 * class can never be changed afterwards — an account denormalises it to enforce its own
 * withdrawability rule, and a product that could switch class would silently reclassify every
 * deposit already reported to the regulator under the other one.
 *
 * **The rate.** The wire carries a decimal fraction between 0 and 1: `0.075` is 7.5%. This form
 * collects a percentage, because that is what a rate is called in every document the operator has
 * in front of them, and divides by a hundred before sending. The conversion goes through basis
 * points as an integer so `7.5` becomes exactly `0.075` and not something a floating-point multiply
 * left one ulp short of it.
 */

const PERCENT_INPUT = /^\d{0,3}(\.\d{0,4})?$/

/** `7.5` → `750` basis points. Rejects anything the pattern above would not let be typed. */
function percentToBasisPoints(raw: string): number | null {
  if (raw.trim() === '' || !PERCENT_INPUT.test(raw.trim())) {
    return null
  }

  const [whole = '0', fraction = ''] = raw.trim().split('.')
  const wholeBasisPoints = Number(whole === '' ? 0 : whole) * 100
  const fractionBasisPoints = fraction === '' ? 0 : Number(fraction.slice(0, 2).padEnd(2, '0'))

  // More than two decimal places on a percentage is finer than a basis point, which is finer than
  // any rate is ever set, and finer than the fraction the server stores can carry meaningfully.
  if (fraction.length > 2) {
    return null
  }

  return wholeBasisPoints + fractionBasisPoints
}

const MAX_BASIS_POINTS = 10_000

/* The server's own bounds, from `CreateSavingsProductValidator`. A form that lets a figure past
   one of these turns a typo into a 400 carrying a code the operator has never seen. */
const MAX_CODE_LENGTH = 24
const MAX_NAME_LENGTH = 160
const MAX_DORMANCY_DAYS = 3650

export function NewProductDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useSavingsPermissions()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [productClass, setProductClass] = useState<SavingsProductClass>('RegularSavings')
  const [isWithdrawable, setIsWithdrawable] = useState(true)
  const [ratePercent, setRatePercent] = useState('')
  const [frequency, setFrequency] = useState<InterestPostingFrequency>('None')
  const [minOpening, setMinOpening] = useState<Pesewas | null>(0)
  const [minBalance, setMinBalance] = useState<Pesewas | null>(0)
  const [withdrawalFee, setWithdrawalFee] = useState<Pesewas | null>(null)
  const [dormancyDays, setDormancyDays] = useState('365')
  const [liabilityGl, setLiabilityGl] = useState('')
  const [interestExpenseGl, setInterestExpenseGl] = useState('')
  const [feeIncomeGl, setFeeIncomeGl] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const create = useCreateSavingsProduct()

  useEffect(() => {
    if (!open) {
      setCode('')
      setName('')
      setProductClass('RegularSavings')
      setIsWithdrawable(true)
      setRatePercent('')
      setFrequency('None')
      setMinOpening(0)
      setMinBalance(0)
      setWithdrawalFee(null)
      setDormancyDays('365')
      setLiabilityGl('')
      setInterestExpenseGl('')
      setFeeIncomeGl('')
      setResult(null)
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ADR-007 makes the class and the withdrawability one decision. A security deposit product that
  // was withdrawable would be collateral the customer could take back, and the server refuses the
  // combination with savings.product_class_not_withdrawable — so the control follows the class
  // rather than letting somebody build a request that cannot succeed.
  const securityDeposit = productClass === 'SecurityDeposit'
  const effectivelyWithdrawable = securityDeposit ? false : isWithdrawable

  const basisPoints = ratePercent.trim() === '' ? 0 : percentToBasisPoints(ratePercent)
  const rateValid = basisPoints !== null && basisPoints <= MAX_BASIS_POINTS
  const interestNeverPosts = rateValid && basisPoints > 0 && frequency === 'None'
  // `InclusiveBetween(0, 3650)` on the server — ten years. Five digits would let 99999 through to
  // a `savings.dormancy_days_out_of_range` the operator cannot act on.
  const dormancyValid =
    /^\d{1,4}$/.test(dormancyDays.trim()) && Number(dormancyDays.trim()) <= MAX_DORMANCY_DAYS

  const trimmedCode = code.trim()
  const trimmedName = name.trim()

  return (
    <SavingsActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Define a savings product"
      description="The terms every account opened against it will carry. The class can never be changed afterwards."
      notice={
        <SavingsDualControlNotice
          action="Defining a product"
          approvePermission={SAVINGS_PERMISSIONS.productManageApprove}
          canCountersign={permissions.canCountersignProduct}
        />
      }
      outcome={
        result ? (
          <SavingsDualControlOutcome
            result={result}
            awaitingDescription={`Defining ${trimmedCode} is waiting for a countersignature. The product does not exist yet and no account can be opened against it.`}
            appliedDescription={`${trimmedCode} — ${trimmedName} exists and can take new accounts.`}
          />
        ) : null
      }
      confirmLabel="Define product"
      isPending={create.isPending}
      disabled={
        trimmedCode === '' || trimmedName === '' || !rateValid || interestNeverPosts ||
        !dormancyValid || minOpening === null || minBalance === null
      }
      error={create.isError ? create.error : null}
      onConfirm={() => {
        if (basisPoints === null || minOpening === null || minBalance === null) {
          return
        }

        create.mutate(
          {
            code: trimmedCode,
            name: trimmedName,
            productClass,
            isWithdrawable: effectivelyWithdrawable,
            // Basis points to the 0–1 fraction the contract expects. 750 → 0.075.
            interestRatePerAnnum: basisPoints / MAX_BASIS_POINTS,
            interestPostingFrequency: frequency,
            minOpeningBalanceMinor: minOpening,
            minBalanceMinor: minBalance,
            withdrawalFeeMinor: withdrawalFee,
            dormancyDays: Number(dormancyDays.trim()),
            currencyCode: BASE_CURRENCY,
            liabilityGlAccountCode: liabilityGl.trim() === '' ? null : liabilityGl.trim(),
            interestExpenseGlAccountCode:
              interestExpenseGl.trim() === '' ? null : interestExpenseGl.trim(),
            feeIncomeGlAccountCode: feeIncomeGl.trim() === '' ? null : feeIncomeGl.trim(),
          },
          { onSuccess: setResult },
        )
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="product-code">Code</Label>
          <Input
            id="product-code"
            value={code}
            autoComplete="off"
            spellCheck={false}
            placeholder="SAV-ORD"
            maxLength={MAX_CODE_LENGTH}
            onChange={(event) => setCode(event.target.value)}
            aria-describedby="product-code-help"
          />
          <p id="product-code-help" className="text-xs text-muted">
            Upper-cased and unique across the institution.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-name">Name</Label>
          <Input
            id="product-name"
            value={name}
            autoComplete="off"
            placeholder="Ordinary passbook savings"
            maxLength={MAX_NAME_LENGTH}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="product-class">Class</Label>
        <Select
          value={productClass}
          onValueChange={(next) => setProductClass(next as SavingsProductClass)}
        >
          <SelectTrigger id="product-class">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SAVINGS_PRODUCT_CLASSES.map((candidate) => (
              <SelectItem key={candidate} value={candidate}>
                {productClassLabel(candidate)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">{productClassDescription(productClass)}</p>
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id="product-withdrawable"
          checked={effectivelyWithdrawable}
          disabled={securityDeposit}
          onCheckedChange={(checked) => setIsWithdrawable(checked === true)}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <Label htmlFor="product-withdrawable" className="text-sm font-normal text-body">
            Withdrawable
          </Label>
          <p className="text-xs text-muted">
            {securityDeposit
              ? 'A security deposit is never withdrawable — the class and the withdrawability are one decision, not two that can disagree.'
              : 'Whether a teller may pay money out of an account on this product.'}
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="product-rate">Interest rate (% per annum)</Label>
          <Input
            id="product-rate"
            value={ratePercent}
            inputMode="decimal"
            autoComplete="off"
            placeholder="7.5"
            onChange={(event) => {
              if (event.target.value === '' || PERCENT_INPUT.test(event.target.value)) {
                setRatePercent(event.target.value)
              }
            }}
            aria-invalid={!rateValid}
            aria-describedby="product-rate-help"
          />
          <p id="product-rate-help" className="text-xs text-muted">
            Typed as a percentage — 7.5 means seven and a half per cent. It crosses the wire as the
            fraction 0.075, which is what the server stores and accrues on. At most two decimal
            places, and never above 100.
          </p>
          {!rateValid ? (
            <p className="text-xs text-danger-text">
              A rate is a percentage between 0 and 100, to at most two decimal places. Above 100 the
              server refuses it with savings.interest_rate_out_of_range.
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-frequency">Interest posting frequency</Label>
          <Select
            value={frequency}
            onValueChange={(next) => setFrequency(next as InterestPostingFrequency)}
          >
            <SelectTrigger id="product-frequency" aria-invalid={interestNeverPosts}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTEREST_POSTING_FREQUENCIES.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {interestFrequencyLabel(candidate)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {interestNeverPosts ? (
            <p className="text-xs text-danger-text">
              A rate above zero with no posting frequency is interest that accrues forever and never
              reaches the customer. The server refuses it with
              savings.product_interest_never_posts — set a frequency, or set the rate to zero.
            </p>
          ) : (
            <p className="text-xs text-muted">
              When capitalised interest is posted. Nothing posts on its own — an interest run has to
              be made for the product at each branch.
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="product-min-opening">Minimum opening balance</Label>
          <MoneyInput id="product-min-opening" value={minOpening} onChange={setMinOpening} />
          <p className="text-xs text-muted">
            Checked on the first customer payment, not at opening — an account with no movement has
            not met a minimum of anything.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-min-balance">Minimum balance</Label>
          <MoneyInput id="product-min-balance" value={minBalance} onChange={setMinBalance} />
          <p className="text-xs text-muted">The floor a withdrawal may not take an account below.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-withdrawal-fee">Withdrawal fee (optional)</Label>
          <MoneyInput
            id="product-withdrawal-fee"
            value={withdrawalFee}
            onChange={setWithdrawalFee}
            disabled={!effectivelyWithdrawable}
          />
          <p className="text-xs text-muted">
            Empty means no fee, which is not the same as a fee of zero. A fee posts as its own
            statement line with its own transaction number every time money is paid out.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-dormancy">Dormancy (idle days)</Label>
          <Input
            id="product-dormancy"
            value={dormancyDays}
            inputMode="numeric"
            autoComplete="off"
            onChange={(event) => setDormancyDays(event.target.value)}
            aria-invalid={!dormancyValid}
            aria-describedby="product-dormancy-help"
          />
          {!dormancyValid ? (
            <p className="text-xs text-danger-text">
              A whole number of days, at most {MAX_DORMANCY_DAYS} — ten years. The server refuses
              anything else with savings.dormancy_days_out_of_range.
            </p>
          ) : null}
          <p id="product-dormancy-help" className="text-xs text-muted">
            {securityDeposit
              ? 'A security deposit never ages into dormancy whatever is set here — it is not idle, it is doing its job.'
              : 'Days without a customer movement before a sweep ages the account into dormancy. Zero means never.'}
          </p>
        </div>
      </div>

      <Alert tone="info">
        <Info aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Only one of the three ledger accounts follows the class</AlertTitle>
          <AlertDescription>
            Left blank, the <strong>liability</strong> account is chosen from the class — regular
            savings and security deposits sit on separate liabilities because the Bank of Ghana
            deposit return reports them separately. The other two do not: interest expense falls back
            to the institution&apos;s interest-expense-on-savings account and fee income to the
            standard account-service fee income code, identically for both classes. Override any of
            them only against the chart of accounts.
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="product-gl-liability">Liability account</Label>
          <Input
            id="product-gl-liability"
            value={liabilityGl}
            autoComplete="off"
            spellCheck={false}
            placeholder={securityDeposit ? 'defaults to security deposits' : 'defaults by class'}
            onChange={(event) => setLiabilityGl(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="product-gl-interest">Interest expense account</Label>
          <Input
            id="product-gl-interest"
            value={interestExpenseGl}
            autoComplete="off"
            spellCheck={false}
            placeholder="defaults institution-wide"
            onChange={(event) => setInterestExpenseGl(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="product-gl-fee">Fee income account</Label>
          <Input
            id="product-gl-fee"
            value={feeIncomeGl}
            autoComplete="off"
            spellCheck={false}
            placeholder="defaults institution-wide"
            onChange={(event) => setFeeIncomeGl(event.target.value)}
          />
        </div>
      </div>
    </SavingsActionDialog>
  )
}
